<template>
  <Drawer
    class="claude-guide"
    :show="show"
    :props="{
      title: 'Handleiding',
      subtitle: 'Claude',
      icon: 'file',
      description: 'Zo koppel je Claude, en wat je daarna kunt vragen.',
    }"
    @close="$emit('close')"
  >
    <div class="claude-guide__body">
      <section>
        <h3>Koppelen</h3>
        <p>
          Claude praat met één organisatie. Je plakt geen API-sleutel. Je logt in met je
          FunnelFlow-account.
        </p>
        <ol>
          <li>
            Ga naar
            <a href="https://claude.ai" target="_blank" rel="noopener noreferrer">claude.ai</a>
            en open <strong>Settings → Connectors</strong>.
          </li>
          <li>Kies <strong>Add custom connector</strong>.</li>
          <li>Naam: FunnelFlow.</li>
          <li>
            URL:
            <code>https://mcp.funnelflow.nl/mcp</code>
          </li>
          <li>Laat het authenticatieveld leeg. Geen header, geen sleutel.</li>
          <li>Log in met het e-mailadres en wachtwoord van je FunnelFlow-account.</li>
          <li>Kies de organisatie als je er meer dan één hebt.</li>
          <li>
            Kies <strong>Assistent</strong>. Dat is offertes, klanten, taken, facturen, service en
            rapportage.
          </li>
          <li>Klik <strong>Toestaan</strong>.</li>
        </ol>
        <p>
          Eén koppeling is één keuze. De catalogus is voor het inrichten van de prijslijst, niet
          voor dagelijks gebruik.
        </p>
      </section>

      <section>
        <h3>Klanten</h3>
        <ul>
          <li>Een klant opzoeken op naam, e-mail, telefoon of bedrijfsnaam, inclusief adres.</li>
          <li>Een notitie of een gesprek vastleggen. Dat komt op de tijdlijn.</li>
          <li>Een taak maken, bijvoorbeeld terugbellen volgende week donderdag.</li>
        </ul>
        <p>Bellen kan Claude niet. Hij geeft het telefoonnummer terug.</p>
      </section>

      <section>
        <h3>Offertes</h3>
        <ul>
          <li>Open offertes bekijken: hoe lang ze eruit staan, en welke binnenkort verlopen.</li>
          <li>Een conceptofferte maken, met één of meer producten en maten.</li>
          <li>
            Die conceptofferte mailen. Claude toont eerst ontvanger en bedrag. De mail gaat pas weg
            nadat je ja zegt.
          </li>
        </ul>
        <p>Prijzen komen uit de configurator. Een al verstuurde offerte gaat niet opnieuw weg.</p>
      </section>

      <section>
        <h3>Cijfers</h3>
        <p>Vraag om een jaar of een periode. Eén antwoord dekt alle maanden, tot achttien maanden.</p>
        <ul>
          <li>Afspraken per maand: montage, inmeting, service en overig. Geannuleerd telt niet mee.</li>
          <li>Offerteaanvragen die die maand zijn binnengekomen.</li>
          <li>Aangevraagde offertewaarde, exclusief btw. Vervangen versies tellen niet mee.</li>
          <li>
            Factuuromzet, inclusief btw. Concepten en geannuleerde facturen tellen niet mee,
            creditnota's wel. Betaald is wat op de facturen van die maand is betaald.
          </li>
          <li>
            Verkochte producten: aantal en omzet exclusief btw. Dat is een geaccepteerde offerte,
            dezelfde regel als Verkocht op het dashboard.
          </li>
        </ul>
      </section>

      <section>
        <h3>Dag, projecten en planning</h3>
        <ul>
          <li>De dag: wie waar rijdt, welke offertes openstaan, welke facturen vervallen zijn.</li>
          <li>Een project opzoeken, met status, klant, orders en werkbonnen.</li>
          <li>
            Een werkbon op een dag bij een monteur zetten. Claude stelt eerst het tijdslot voor. De
            agenda verandert pas na een ja.
          </li>
          <li>Een route voorstellen, en die pas toepassen na een ja.</li>
        </ul>
      </section>

      <section>
        <h3>Facturen</h3>
        <ul>
          <li>Facturen zoeken, en de regels en betalingen bekijken.</li>
          <li>De PDF ophalen. Een concept heeft nog geen PDF.</li>
          <li>
            Een factuur mailen. Claude toont eerst ontvanger en onderwerp. De mail gaat pas weg
            nadat je ja zegt.
          </li>
        </ul>
      </section>

      <section>
        <h3>Service</h3>
        <ul>
          <li>
            Klanten die een tijd geleden iets hebben laten plaatsen en sindsdien geen onderhoud
            hebben gehad.
          </li>
          <li>Open servicemeldingen in de buurt van een monteur of een adres.</li>
        </ul>
      </section>

      <section>
        <h3>Wat hij niet doet</h3>
        <p>
          De assistent bouwt geen funnel, past geen ontwerp of automations aan, en beheert geen
          gebruikers. De prijslijst wijzigen hoort bij de catalogus-koppeling.
        </p>
      </section>

      <section>
        <h3>Nieuwe acties zien</h3>
        <p>
          Na een update houdt Claude de oude lijst vast. Open de connector, kies het menu naast
          <strong>Disconnect</strong> en dan <strong>Refresh tools list</strong>. Stel de vraag
          daarna in een nieuw gesprek.
        </p>
      </section>

      <section>
        <h3>Loskoppelen</h3>
        <p>
          In FunnelFlow: <strong>Organisatie → API</strong>. De sleutel heet
          <strong>Claude assistent</strong>. Intrekken verbreekt de connector.
        </p>
        <p>In Claude: Settings → Connectors → FunnelFlow → verwijderen.</p>
      </section>
    </div>
  </Drawer>
</template>

<script>
  import Drawer from '@/components/drawer/Drawer.vue';

  export default {
    name: 'ClaudeGuideDrawer',

    components: { Drawer },

    props: {
      show: { type: Boolean, default: false },
    },

    emits: ['close'],
  };
</script>

<style lang="scss">
  .drawer.claude-guide {
    width: min(560px, 100vw);
    right: -100vw;

    &.drawer--open {
      right: 0;
    }
  }
</style>

<style scoped lang="scss">
  .claude-guide__body {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    color: var(--slate-700);
    font-size: 0.875rem;
    line-height: 1.5;

    h3 {
      margin: 0 0 0.5rem;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    p {
      margin: 0 0 0.5rem;
    }

    ol,
    ul {
      margin: 0;
      padding-left: 1.25rem;
    }

    li + li {
      margin-top: 0.35rem;
    }

    a {
      color: var(--brand-600);
    }

    code {
      font-size: 0.8125rem;
      color: var(--slate-900);
    }
  }
</style>
